import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Award, CheckCircle2, ClipboardList, Lock, Printer, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ASSESSMENTS = [
  {
    key: "grades-1-8",
    title: "Math Assessment — Grades 1–8",
    grades: "Elementary & Middle School",
    blurb: "Arithmetic, fractions, decimals, percentages, ratios, and geometry — a full elementary-through-middle-school checkup.",
    questions: [
      { q: "47 + 38 = ?", options: ["75", "85", "83", "95"], answer: 1 },
      { q: "90 − 56 = ?", options: ["44", "36", "34", "46"], answer: 2 },
      { q: "7 × 8 = ?", options: ["54", "56", "63", "48"], answer: 1 },
      { q: "144 ÷ 12 = ?", options: ["14", "11", "12", "16"], answer: 2 },
      { q: "What is 1/2 + 1/4?", options: ["2/6", "1/8", "3/4", "1/3"], answer: 2 },
      { q: "What is 0.5 + 0.75?", options: ["1.25", "0.80", "1.20", "0.125"], answer: 0 },
      { q: "What is 25% of 80?", options: ["25", "16", "20", "40"], answer: 2 },
      { q: "Round 4.367 to the nearest tenth.", options: ["4.3", "4.4", "4.37", "4.0"], answer: 1 },
      { q: "What is the perimeter of a rectangle that is 8 units long and 5 units wide?", options: ["13", "40", "26", "21"], answer: 2 },
      { q: "What is 3/5 written as a decimal?", options: ["0.35", "0.53", "0.6", "3.5"], answer: 2 },
      { q: "If 3 pencils cost $1.50, how much do 6 pencils cost?", options: ["$2.50", "$3.00", "$4.50", "$6.00"], answer: 1 },
      { q: "What is the value of 2³ + 1?", options: ["7", "9", "8", "65"], answer: 1 },
    ],
  },
  {
    key: "grades-9-12",
    title: "Math Assessment — Grades 9–12",
    grades: "High School",
    blurb: "Quadratics, logarithms, trigonometry, functions, and probability — measure your readiness for high-school math and beyond.",
    questions: [
      { q: "Solve: x² − 5x + 6 = 0", options: ["x = 1 or 6", "x = 2 or 3", "x = −2 or −3", "x = 5 or 6"], answer: 1 },
      { q: "What is log₂(32)?", options: ["4", "5", "6", "16"], answer: 1 },
      { q: "In a right triangle, the side opposite angle θ is 6 and the hypotenuse is 10. What is sin θ?", options: ["0.8", "0.6", "0.75", "1.67"], answer: 1 },
      { q: "Simplify: √72", options: ["6√2", "8√3", "36", "4√6"], answer: 0 },
      { q: "What is the slope of a line perpendicular to y = 2x + 1?", options: ["2", "1/2", "−2", "−1/2"], answer: 3 },
      { q: "Solve: |x − 3| = 5", options: ["x = 8 only", "x = 8 or x = −2", "x = 2 or x = −8", "x = 5 or x = −5"], answer: 1 },
      { q: "What is the vertex of the parabola y = (x − 2)² + 3?", options: ["(−2, 3)", "(2, −3)", "(2, 3)", "(3, 2)"], answer: 2 },
      { q: "If f(x) = 3x − 1, what is f(f(2))?", options: ["11", "14", "17", "5"], answer: 1 },
      { q: "What is the probability of rolling a 4 on a fair six-sided die?", options: ["1/4", "1/6", "1/3", "4/6"], answer: 1 },
      { q: "Simplify: (2x²y)³", options: ["6x⁵y³", "8x⁶y³", "8x⁵y⁴", "2x⁶y³"], answer: 1 },
      { q: "What is the sum of the interior angles of a hexagon?", options: ["540°", "360°", "720°", "900°"], answer: 2 },
      { q: "Solve: 2^(x + 1) = 16", options: ["x = 3", "x = 4", "x = 7", "x = 15"], answer: 0 },
    ],
  },
  {
    key: "algebra",
    title: "General Algebra Assessment",
    grades: "Algebra I & II",
    blurb: "Linear equations, factoring, functions, and systems — find out exactly where your algebra skills stand.",
    questions: [
      { q: "Solve for x: 3x + 7 = 22", options: ["x = 3", "x = 5", "x = 7", "x = 9"], answer: 1 },
      { q: "Simplify: 4(x − 3) + 2x", options: ["6x − 12", "6x − 3", "8x − 12", "2x − 12"], answer: 0 },
      { q: "What is the slope of the line through (2, 3) and (6, 11)?", options: ["1/2", "1", "2", "4"], answer: 2 },
      { q: "Solve for x: 2x − 5 = 9", options: ["x = 2", "x = 7", "x = 14", "x = 4"], answer: 1 },
      { q: "Factor completely: x² + 5x + 6", options: ["(x + 1)(x + 6)", "(x + 2)(x + 3)", "(x − 2)(x − 3)", "(x + 5)(x + 1)"], answer: 1 },
      { q: "If y = 2x² − 3x + 1, what is y when x = 2?", options: ["1", "3", "5", "7"], answer: 1 },
      { q: "Solve the inequality: 5x − 3 > 12", options: ["x > 3", "x < 3", "x > 9/5", "x ≥ 3"], answer: 0 },
      { q: "Simplify: x³ · x⁴", options: ["x¹²", "x⁷", "2x⁷", "x"], answer: 1 },
      { q: "Solve the system: x + y = 10 and x − y = 4", options: ["x = 6, y = 4", "x = 7, y = 3", "x = 5, y = 5", "x = 8, y = 2"], answer: 1 },
      { q: "Solve: x² = 49", options: ["x = 7 only", "x = −7 only", "x = 7 or x = −7", "x = 49"], answer: 2 },
    ],
  },
];

function placementFor(pct) {
  if (pct >= 80) return { label: "Strong", note: "Excellent command of this material. Advanced enrichment or test-prep sessions will keep these skills sharp." };
  if (pct >= 50) return { label: "Developing", note: "A solid base with a few gaps. A short series of focused sessions will close them quickly." };
  return { label: "Foundations", note: "This is the perfect place to start. Step-by-step tutoring will build these skills from the ground up — no judgment, just progress." };
}

export default function MathAssessments() {
  const [active, setActive] = useState(null); // assessment object
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]); // chosen option index per question
  const [done, setDone] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [showReview, setShowReview] = useState(false);
  const [showCert, setShowCert] = useState(false);

  const start = (a) => { setActive(a); setStep(0); setAnswers([]); setDone(false); setSent(false); setName(""); setEmail(""); setShowReview(false); };
  const quit = () => { setActive(null); setDone(false); };

  const choose = (i) => {
    const next = [...answers];
    next[step] = i;
    setAnswers(next);
  };

  const correctCount = active ? active.questions.filter((q, i) => answers[i] === q.answer).length : 0;
  const pct = active ? Math.round((correctCount / active.questions.length) * 100) : 0;
  const placement = placementFor(pct);

  const sendResults = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const missed = active.questions
        .map((q, i) => (answers[i] === q.answer ? null : `Q${i + 1}: ${q.q} — student answered "${q.options[answers[i]] ?? "skipped"}", correct: "${q.options[q.answer]}"`))
        .filter(Boolean);
      const message = [
        `Tutoring Assessment Results — ${active.title}`,
        `Score: ${correctCount}/${active.questions.length} (${pct}%) — Placement: ${placement.label}`,
        "",
        missed.length ? `Questions to review:\n${missed.join("\n")}` : "The student answered every question correctly.",
        "",
        "Please contact me about tutoring.",
      ].join("\n");
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), message }),
      });
      if (!res.ok) throw new Error("send failed");
      setSent(true);
      toast.success("Results sent to Dr. Louden");
    } catch {
      toast.error("Could not send results. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="math-assessments" className="mt-14">
      <span className="text-xs uppercase tracking-[0.4em] text-[#A78BFA]">Free Placement Check</span>
      <h2 className="mt-3 font-display text-4xl md:text-6xl uppercase tracking-tight text-white">
        Tutoring Assessments
      </h2>
      <p className="mt-4 max-w-2xl text-base text-white/60">
        Choose your level — Grades 1–8, Grades 9–12, or the General Algebra Assessment. Your score shows
        exactly where to begin, and you can send it straight to Dr. Louden so your first lesson starts at
        the right level.
      </p>

      <AnimatePresence mode="wait">
        {!active && (
          <motion.div key="picker" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="mt-10 grid gap-6 md:grid-cols-3">
            {ASSESSMENTS.map((a) => (
              <div key={a.key} data-testid={`assessment-card-${a.key}`}
                className="flex flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-7 transition-colors hover:border-[#7C3AED]/50">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA]">{a.grades}</span>
                <h3 className="mt-3 font-display text-2xl uppercase text-white">{a.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-white/55">{a.blurb}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="text-xs text-white/40">{a.questions.length} questions · ~5 min</span>
                  <button onClick={() => start(a)} data-testid={`assessment-start-${a.key}`}
                    className="magnetic-btn inline-flex items-center gap-2 rounded-full bg-[#7C3AED] px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black">
                    Start <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {active && !done && (
          <motion.div key="quiz" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="mt-10 rounded-2xl border border-[#7C3AED]/30 bg-white/[0.03] p-7 md:p-10" data-testid="assessment-quiz">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button onClick={quit} data-testid="assessment-back-btn"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 hover:text-white">
                <ArrowLeft size={14} /> All assessments
              </button>
              <span className="text-xs uppercase tracking-widest text-[#A78BFA]" data-testid="assessment-progress">
                {active.title} — Question {step + 1} of {active.questions.length}
              </span>
            </div>
            <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-[#7C3AED] transition-all duration-300"
                style={{ width: `${((step + 1) / active.questions.length) * 100}%` }} />
            </div>

            <h3 className="mt-8 text-xl font-semibold text-white md:text-2xl" data-testid="assessment-question">
              {active.questions[step].q}
            </h3>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {active.questions[step].options.map((opt, i) => (
                <button key={i} onClick={() => choose(i)} data-testid={`assessment-option-${i}`}
                  className={`rounded-xl border px-5 py-4 text-left text-sm transition-colors ${
                    answers[step] === i
                      ? "border-[#7C3AED] bg-[#7C3AED]/20 text-white"
                      : "border-white/10 bg-white/[0.02] text-white/70 hover:border-[#7C3AED]/50 hover:text-white"
                  }`}>
                  <span className="mr-2 font-semibold text-[#A78BFA]">{String.fromCharCode(65 + i)}.</span>{opt}
                </button>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                data-testid="assessment-prev-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest text-white/60 transition-colors hover:text-white disabled:opacity-30">
                <ArrowLeft size={14} /> Back
              </button>
              {step < active.questions.length - 1 ? (
                <button onClick={() => answers[step] !== undefined && setStep(step + 1)} disabled={answers[step] === undefined}
                  data-testid="assessment-next-btn"
                  className="magnetic-btn inline-flex items-center gap-2 rounded-full bg-[#7C3AED] px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black disabled:opacity-30">
                  Next <ArrowRight size={14} />
                </button>
              ) : (
                <button onClick={() => answers[step] !== undefined && setDone(true)} disabled={answers[step] === undefined}
                  data-testid="assessment-finish-btn"
                  className="magnetic-btn inline-flex items-center gap-2 rounded-full bg-[#7C3AED] px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black disabled:opacity-30">
                  See My Results <CheckCircle2 size={14} />
                </button>
              )}
            </div>
          </motion.div>
        )}

        {active && done && (
          <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="mt-10 rounded-2xl border border-[#7C3AED]/30 bg-white/[0.03] p-7 md:p-10" data-testid="assessment-results">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#A78BFA]">{active.title} — Complete</span>

            <div className="mt-6 rounded-2xl border border-[#7C3AED]/40 bg-[#0D0D12] px-8 py-10 text-center" data-testid="assessment-score-panel">
              <div className="text-xs uppercase tracking-[0.35em] text-white/50">Your Score</div>
              <div className="mt-3 font-display text-8xl text-white" data-testid="assessment-score">{pct}%</div>
              <div className="mt-4 inline-flex items-center gap-3">
                <span className="rounded-full bg-[#7C3AED]/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#A78BFA]" data-testid="assessment-placement">
                  {placement.label}
                </span>
                <span className="text-sm text-white/50">{correctCount} of {active.questions.length} correct</span>
              </div>
              <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-white/60">{placement.note}</p>
              <p className="mx-auto mt-6 flex max-w-xl items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-5 py-2.5 text-xs text-white/55" data-testid="assessment-privacy-note">
                <Lock size={13} className="shrink-0 text-[#A78BFA]" />
                Private: this score appears only on your screen. It is not saved or shared unless you choose to send it below.
              </p>
            </div>

            <div className="mt-6 text-center">
              <button onClick={() => setShowReview((v) => !v)} data-testid="assessment-review-toggle"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 text-xs uppercase tracking-widest text-white/70 transition-colors hover:text-white">
                {showReview ? "Hide answer review" : "Review my answers"}
              </button>
            </div>

            {showReview && (
            <div className="mt-6 space-y-2">
              {active.questions.map((q, i) => (
                <div key={i} className="flex items-start gap-3 text-sm" data-testid={`assessment-review-${i}`}>
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                    answers[i] === q.answer ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
                  }`}>
                    {answers[i] === q.answer ? "✓" : "✗"}
                  </span>
                  <span className="text-white/60">
                    {q.q}
                    {answers[i] !== q.answer && (
                      <span className="block text-xs text-white/40">
                        Your answer: {q.options[answers[i]] ?? "—"} · Correct: {q.options[q.answer]}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
            )}

            <div className="mt-10 rounded-xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-white">
                <ClipboardList size={16} className="text-[#A78BFA]" /> Send my results to Dr. Louden
              </div>
              <p className="mt-2 text-xs text-white/50">
                He will review your answers personally and recommend the right starting point for your sessions.
              </p>
              {sent ? (
                <p className="mt-4 text-sm font-semibold text-emerald-400" data-testid="assessment-sent-msg">
                  Results sent — Dr. Louden will reply to your email shortly.
                </p>
              ) : (
                <form onSubmit={sendResults} className="mt-4 flex flex-col gap-3 sm:flex-row" data-testid="assessment-send-form">
                  <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                    data-testid="assessment-name-input"
                    className="w-full rounded-full border border-white/15 bg-black/40 px-5 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-[#7C3AED]" />
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
                    data-testid="assessment-email-input"
                    className="w-full rounded-full border border-white/15 bg-black/40 px-5 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-[#7C3AED]" />
                  <button type="submit" disabled={sending} data-testid="assessment-send-btn"
                    className="magnetic-btn inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#7C3AED] px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50">
                    <Send size={13} /> {sending ? "Sending…" : "Send Results"}
                  </button>
                </form>
              )}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={() => setShowCert(true)} data-testid="assessment-certificate-btn"
                className="magnetic-btn inline-flex items-center gap-2 rounded-full bg-[#C9A227] px-6 py-3 text-xs font-bold uppercase tracking-widest text-black transition-colors hover:bg-white">
                <Award size={14} /> Get My Certificate
              </button>
              <button onClick={quit} data-testid="assessment-retake-picker-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs uppercase tracking-widest text-white/70 transition-colors hover:text-white">
                <RotateCcw size={14} /> Take another assessment
              </button>
            </div>

            {showCert && (
              <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4" onClick={() => setShowCert(false)}>
                <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
                  <div id="certificate-print" data-testid="certificate-card"
                    className="rounded-lg border-4 border-[#C9A227] bg-[#0b0b0f] p-10 text-center md:p-14">
                    <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A227]">Delta DL Enterprise, LLC</div>
                    <h3 className="mt-4 font-display text-3xl uppercase tracking-tight text-white md:text-4xl">
                      Certificate of Achievement
                    </h3>
                    <div className="mx-auto mt-4 h-px w-40 bg-[#C9A227]" />
                    <p className="mt-6 text-sm text-white/60">This certifies that</p>
                    <p data-testid="certificate-name" className="mt-2 font-display text-2xl text-[#C9A227] md:text-3xl">
                      {name.trim() || "____________________"}
                    </p>
                    <p className="mt-6 text-sm leading-relaxed text-white/60">
                      has successfully completed the <span className="text-white">{active.title}</span>
                      <br />with a score of <span className="text-white">{pct}%</span> — {placement.label} level
                    </p>
                    <p className="mt-6 text-xs text-white/40">
                      {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                    <div className="mx-auto mt-8 max-w-xs border-t border-white/30 pt-2">
                      <p className="text-sm italic text-white/70">Dr. Dennis Louden</p>
                      <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Founder & Instructor</p>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
                    {!name.trim() && (
                      <p className="w-full text-center text-xs text-amber-400">
                        Tip: enter your name in the "Send my results" form first so it prints on the certificate.
                      </p>
                    )}
                    <button onClick={() => window.print()} data-testid="certificate-print-btn"
                      className="magnetic-btn inline-flex items-center gap-2 rounded-full bg-[#C9A227] px-8 py-3 text-xs font-bold uppercase tracking-widest text-black transition-colors hover:bg-white">
                      <Printer size={14} /> Print / Save as PDF
                    </button>
                    <button onClick={() => setShowCert(false)} data-testid="certificate-close-btn"
                      className="rounded-full border border-white/25 px-6 py-3 text-xs uppercase tracking-widest text-white/70 transition-colors hover:text-white">
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
